import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import { findRecord, recordKeyColumns } from "@/profitability/records/objects";
import { useFinding, useFindings } from "@/profitability/review/query";
import { useRecordHistory } from "./history-query";
import type { RecordPeriod } from "./record-missing";

/**
 * One record: its row in the latest report, and its finding when flagged. A
 * job flagged in an earlier week is not in the latest report; its row is the
 * one the finding kept from that week.
 */
export const useRecord = (
  businessId: string,
  reportId: string,
  objects: RecordObject[],
  ref: RecordRef,
  period: RecordPeriod | null = null,
) => {
  const object = objects.find((item) => item.dataset.id === ref.dataset);
  const latest = object ? findRecord(object.dataset, ref.recordId) : undefined;
  const findings = useFindings(businessId, reportId, "all", {
    dataset: ref.dataset,
    recordIds: [ref.recordId],
  });
  const candidate = findings.data?.pages[0]?.findings[0] ?? null;
  // A future finding must not supply a snapshot or lifecycle state to a past period.
  const finding =
    candidate &&
    period?.dates &&
    candidate.end_date &&
    candidate.end_date > period.dates.end_date
      ? null
      : candidate;
  const detail = useFinding(businessId, finding?.id ?? null);
  // Records with an id have a history; it holds the row from an earlier
  // period when the latest report no longer lists the record.
  const history = useRecordHistory(
    businessId,
    reportId,
    ref,
    !!object && recordKeyColumns(object.dataset).length > 0,
    period?.runId,
  );
  const earlier = [...(history.data?.periods ?? [])]
    .reverse()
    .find((period) => period.row);
  // Prefer the most recent historical row over the last time it was flagged.
  const previous =
    earlier ??
    (finding
      ? {
          row: finding.snapshot,
          start_date: finding.start_date,
          end_date: finding.end_date,
          run_id: finding.run_id,
        }
      : null);
  const row = latest ?? previous?.row ?? undefined;
  const week = latest ? null : previous;
  const weekRunId = latest ? null : (previous?.run_id ?? null);
  // The id is opaque, never shown: a record not found reads by its kind.
  const name =
    object && row
      ? String(row[object.nameColumn.key] ?? object.name)
      : (object?.name ?? "Record");
  return {
    object,
    row,
    name,
    week,
    weekRunId,
    loading: !latest && (findings.isPending || history.isPending),
    history: history.data?.periods ?? [],
    finding,
    activity: detail.data?.activity ?? [],
  };
};

export type RecordData = ReturnType<typeof useRecord>;
